// Type-only import for type annotations
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import type { AvailabilityCircle } from '../ts/availabilityCircle';
import { formatDateAsUTC, zip } from '../ts/common';

export class Calendar extends HTMLElement {
    static observedAttributes: string[] = [
        'outbound', 'outbound-iata', 'inbound', 'inbound-iata', 'airline',
        'show-cabin-diffs', 'show-never-flights-dates', 'override-date-range', 'show-collection-details', 'single-column', 'allow-historic-dates',
        'force-inbound', 'heatmap', 'trip-duration-min', 'trip-duration-max', 'independent-legs', 'inline-search-summary'
    ];

    oneWay: boolean = false;
    availableCabins: Cabin[] = [];
    selectedCabins: Cabin[] = [];
    cabins: Cabin[] = [];
    flightRouteCabins: Cabin[][] = [];
    cabinDisplayNameMapping?: CabinDisplayNameMapping;
    showClubSuiteOnlyToggle: boolean = false;
    showCompanionVoucherFilter: boolean = false;
    message?: string;
    neverFlightDates?: string[][][];
    days: AvailabilityCircle[] = [];
    copy?: Calendar;
    maxPoints?: number;
    cabinCounts: Map<string, Map<Cabin, number[]>>;
    numMonths: number = 0;
    firstDayWithData: boolean = true;
    bookingOutPicker?: FlatpickrInstance;
    bookingInPicker?: FlatpickrInstance;

    #outboundStartDate?: Date;
    #inboundStartDate?: Date;
    #outboundEndDate?: Date;
    #inboundEndDate?: Date;
    #startDate?: Date;
    #endDate?: Date;
    #convertPointsHeading: () => string = () => { return "Points"; };
    #adminCircle: SVGCircleElement = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    #data: RetrieveYearDataResponse[] | GetFlightAvailabilityResponse[] = [];
    #datacopy: RetrieveYearDataResponse[] | GetFlightAvailabilityResponse[] = [];
    #ctas: DocumentFragment[] = [];

    constructor() {
        super();

        const cta1Template = document.getElementById('signup-cta-template') as HTMLTemplateElement;
        const cta2Template = document.getElementById('signup-cta-template-2') as HTMLTemplateElement;
        const cta3Template = document.getElementById('signup-cta-template-3') as HTMLTemplateElement;
        const alertCTATemplate = document.getElementById('alerts-cta-template') as HTMLTemplateElement;
        const companionVoucherCTATemplate = document.getElementById('companion-voucher-cta-template') as HTMLTemplateElement;
        this.#ctas = [
            document.importNode(cta1Template.content, true),
            document.importNode(cta2Template.content, true),
            document.importNode(cta3Template.content, true),
            document.importNode(alertCTATemplate.content, true),
            document.importNode(companionVoucherCTATemplate.content, true)
        ];

        this.#adminCircle.setAttribute("cx", "18.5");
        this.#adminCircle.setAttribute("cy", "18.5");
        this.#adminCircle.setAttribute("r", "10.5");

        this.cabinCounts = new Map<string, Map<Cabin, number[]>>()
        this.cabinCounts.set('outbound', new Map<Cabin, number[]>());
        this.cabinCounts.set('inbound', new Map<Cabin, number[]>());
    }

    connectedCallback() {
        this.copy = this.cloneNode(true) as Calendar;
    }

    attributeChangedCallback(attrName: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (attrName === 'single-column') {  // Used to restrict the calendar to a single column (e.g. for WCIG return searches because outbound and return are created separately)
                this.singleColumn = !(newValue === null); // Explicitly call the setter
            } else if (attrName === 'outbound') {
                this.outbound = newValue;
            } else if (attrName === 'inbound') {
                this.inbound = newValue;
            } else if (attrName === 'outbound-iata') {
                this.outboundIATA = newValue;
            } else if (attrName === 'inbound-iata') {
                this.inboundIATA = newValue;
            } else if (attrName === 'airline') {
                this.airline = newValue;
            } else if (attrName === 'show-cabin-diffs') {
                this.showCabinDiffs = !(newValue === null);
            } else if (attrName === 'show-never-flight-dates') {
                this.showNeverFlightDates = !(newValue === null);
            } else if (attrName === 'override-date-range') {
                this.overrideDateRange = newValue;
            } else if (attrName === 'show-collection-details') {
                this.showCollectionDetails = !(newValue === null);
            } else if (attrName === 'allow-historic-dates') {
                this.allowHistoricDate = !(newValue === null);
            } else if (attrName === 'force-inbound') {
                this.forceInbound = !(newValue === null);
            } else if (attrName === 'heatmap') {
                this.heatmap = !(newValue === null);
            } else if (attrName === 'trip-duration-min') {
                this.tripDurationMin = newValue !== null ? parseInt(newValue) : null;
            } else if (attrName === 'trip-duration-max') {
                this.tripDurationMax = newValue !== null ? parseInt(newValue) : null;
            } else if (attrName === 'independent-legs') {
                this.independentLegs = newValue !== null;
            } else if (attrName === 'inline-search-summary') {
                this.inlineSearchSummary = newValue !== null;
            }
        }
    }

    get singleColumn(): boolean {
        return this.hasAttribute('single-column');
    }

    set singleColumn(value: boolean) {
        if (value) {
            this.setAttribute('single-column', '');
        } else {
            this.removeAttribute('single-column');
        }
    }

    get outbound(): string {
        return this.getAttribute('outbound') || '';
    }

    set outbound(value: string) {
        this.setAttribute('outbound', value);
    }

    get outboundIATA(): string {
        return this.getAttribute('outbound-iata') || '';
    }

    set outboundIATA(value: string) {
        this.setAttribute('outbound-iata', value);
    }

    get inbound(): string {
        return this.getAttribute('inbound') || '';
    }

    set inbound(value: string) {
        this.setAttribute('inbound', value);
    }

    get inboundIATA(): string {
        return this.getAttribute('inbound-iata') || '';
    }

    set inboundIATA(value: string) {
        this.setAttribute('inbound-iata', value);
    }

    get airline() {
        return this.getAttribute('airline') || '';
    }

    set airline(value: string) {
        this.setAttribute('airline', value);
    }

    get showCabinDiffs(): boolean {
        return this.hasAttribute('show-cabin-diffs');
    }

    set showCabinDiffs(value: boolean) {
        if (value) {
            this.setAttribute('show-cabin-diffs', '');
        } else {
            this.removeAttribute('show-cabin-diffs');
        }
    }

    get showNeverFlightDates(): boolean {
        return this.hasAttribute('show-never-flight-dates');
    }

    set showNeverFlightDates(value: boolean) {
        if (value) {
            this.setAttribute('show-never-flight-dates', '');
        } else {
            this.removeAttribute('show-never-flight-dates');
        }
    }

    get overrideDateRange(): string {
        return this.getAttribute('override-date-range') || '';
    }

    set overrideDateRange(value: string) {
        this.setAttribute('override-date-range', value);
    }

    get showCollectionDetails(): boolean {
        return this.hasAttribute('show-collection-details');
    }

    set showCollectionDetails(value: boolean) {
        if (value) {
            this.setAttribute('show-collection-details', '');
        } else {
            this.removeAttribute('show-collection-details');
        }
    }

    get allowHistoricDate(): boolean {
        return this.hasAttribute('allow-historic-dates');
    }

    set allowHistoricDate(value: boolean) {
        if (value) {
            this.setAttribute('allow-historic-dates', '');
        } else {
            this.removeAttribute('allow-historic-dates');
        }
    }

    get forceInbound(): boolean {
        return this.hasAttribute('force-inbound');
    }

    set forceInbound(value: boolean) {
        if (value) {
            this.setAttribute('force-inbound', '');
        } else {
            this.removeAttribute('force-inbound');
        }
    }

    get heatmap(): boolean {
        return this.hasAttribute('heatmap');
    }

    set heatmap(value: boolean) {
        if (value) {
            this.setAttribute('heatmap', '');
        } else {
            this.removeAttribute('heatmap');
        }
    }
    
    get tripDurationMin(): number | null {
        const value = this.getAttribute('trip-duration-min');
        return value !== null ? parseInt(value) : null;
    }

    set tripDurationMin(value: number | null) {
        if (value !== null) {
            this.setAttribute('trip-duration-min', value.toString());
        } else {
            this.removeAttribute('trip-duration-min');
        }
    }

    get tripDurationMax(): number | null {
        const value = this.getAttribute('trip-duration-max');
        return value !== null ? parseInt(value) : null;
    }

    set tripDurationMax(value: number | null) {
        if (value !== null) {
            this.setAttribute('trip-duration-max', value.toString());
        } else {
            this.removeAttribute('trip-duration-max');
        }
    }

    get data(): RetrieveYearDataResponse[] | GetFlightAvailabilityResponse[] | undefined {
        return this.#data;
    }

    set data(value: RetrieveYearDataResponse[] | GetFlightAvailabilityResponse[]) {
        // Reset the private date properties when new data is set, so that they will be recalculated based on the new data
        this.#outboundStartDate = undefined;
        this.#inboundStartDate = undefined;
        this.#outboundEndDate = undefined;
        this.#inboundEndDate = undefined;
        this.#startDate = undefined;
        this.#endDate = undefined;

        this.#data = value;
        this.#datacopy = JSON.parse(JSON.stringify(value));

        this.cabinCounts = new Map<string, Map<Cabin, number[]>>()
        this.cabinCounts.set('outbound', new Map<Cabin, number[]>());
        this.cabinCounts.set('inbound', new Map<Cabin, number[]>());

        this.flightRouteCabins = [...new Set(value.map(d => d.flightRouteCabins))];
        this.availableCabins = [... new Set(value.map(d => d.availableCabins).reduce((a, b) => a.concat(b)))];
        this.selectedCabins = this.availableCabins;
        this.cabins = [... new Set(value.map(d => d.cabins).reduce((a, b) => a.concat(b)))];
        this.cabinDisplayNameMapping = value.map(d => d.cabinDisplayNameMapping).reduce((a, b) => Object.assign(a, b), {
            economy: "Economy",
            premium: "Premium",
            business: "Business",
            first: "First"
        });
        const messages = value.map(d => 'message' in d ? d.message : null).filter(m => m != null);
        this.message = messages.length > 0 ? messages[0] : undefined;
        this.neverFlightDates = value.map(d => 'neverFlightDates' in d ? d.neverFlightDates: []);
        this.oneWay = this.#data.length == 1;
        this.constructCalendar();
    }

    get convertPointsHeading(): () => string {
        return this.#convertPointsHeading;
    }

    set convertPointsHeading(value: () => string) {
        this.#convertPointsHeading = value;
    }

    get independentLegs(): boolean {
        return this.hasAttribute('independent-legs');
    }

    set independentLegs(value: boolean) {
        if (value) {
            this.setAttribute('independent-legs', '');
        } else {
            this.removeAttribute('independent-legs');
        }
    }

    get inlineSearchSummary(): boolean {
        return this.hasAttribute('inline-search-summary');
    }

    set inlineSearchSummary(value: boolean) {
        if (value) {
            this.setAttribute('inline-search-summary', '');
        } else {
            this.removeAttribute('inline-search-summary');
        }
    }

    get latestDate(): Date {
        return this.endDate();
    }

    private startDate(outbound?: boolean): Date {
        const overrideDateRange = this.overrideDateRange ? this.overrideDateRange.split(' - ').map(d => new Date(d + " 00:00:00 GMT").getTime()) : [null, null];

        if (overrideDateRange[0]) {
            return new Date(overrideDateRange[0]);
        }

        const today = new Date(new Date().toISOString().split('T')[0].replace(/-/g, '/') + " 00:00:00 GMT").getTime();

        if (this.independentLegs) {
            if (outbound) {
                if (this.#outboundStartDate === undefined) {
                    this.#outboundStartDate = new Date(Math.max(new Date(this.#data[0].earliestDate + " 00:00:00 GMT").getTime(), today));
                }
                return this.#outboundStartDate;
            } else {
                if (this.#inboundStartDate === undefined) {
                    this.#inboundStartDate = new Date(Math.max(new Date(this.#data[1].earliestDate + " 00:00:00 GMT").getTime(), today));
                }
                return this.#inboundStartDate;
            }
        }

        if (this.#startDate === undefined) {
            const startDate = Math.min(...this.#data!.map(d => new Date(d.earliestDate + " 00:00:00 GMT").getTime()));
            this.#startDate = new Date(Math.max(startDate, today));
        }
        return this.#startDate;
    }

    private endDate(outbound?: boolean): Date {
        const overrideDateRange = this.overrideDateRange ? this.overrideDateRange.split(' - ').map(d => new Date(d + " 00:00:00 GMT").getTime()) : [null, null];

        if (overrideDateRange[1]) {
            return new Date(overrideDateRange[1]);
        }

        if (this.independentLegs) {
            if (outbound) {
                if (this.#outboundEndDate === undefined) {
                    this.#outboundEndDate = new Date(this.#data[0].latestDate + " 00:00:00 GMT");
                }
                return this.#outboundEndDate;
            } else {
                if (this.#inboundEndDate === undefined) {
                    this.#inboundEndDate = new Date(this.#data[1].latestDate + " 00:00:00 GMT");
                }
                return this.#inboundEndDate;
            }
        }

        if (this.#endDate === undefined) {
            const endDate = Math.max(...this.#data!.map(d => new Date(d.latestDate + " 00:00:00 GMT").getTime()));
            this.#endDate = new Date(endDate);
        }
        return this.#endDate;
    }

    filter(filters: Filters) {
        // Copy and replace calendar node
        var start = performance.now();
        var docFrag = document.createDocumentFragment();

        const copy = this.cloneNode(true) as Calendar;
        copy.data = this.#datacopy;

        copy.days.forEach((d) => {
            d.filter(filters);
        });
        docFrag.appendChild(copy);
        requestAnimationFrame(() => {
            this.parentNode!.replaceChild(docFrag, this);
        });
        console.log(performance.now() - start);
        this._calculateMinMaxIntensity(filters);
        console.log(performance.now() - start);
    }

    filter2(filters: Filters) {
        this.selectedCabins = filters.cabins;

        if (this.days.length == 0) {
            return;
        }

        window.requestAnimationFrame(() => {
            this._initialiseCabinCounts();
            // For each date
            this.days.forEach((d) => {
                // Get the date's month index between start and end date
                const index = (d.date!.getFullYear() - this.startDate(d.outbound).getFullYear()) * 12 + (d.date!.getMonth() - this.startDate(d.outbound).getMonth());
                // Filter the date and get the number of flights per cabin
                const cc = d.filter(filters);
                var map = this.cabinCounts!.get(d.outbound ? 'outbound' : 'inbound') as Map<Cabin, number[]>;
                cc.forEach((cabinCount, cabin) => {
                    map.get(cabin)![index] += cabinCount;
                });
            });
            this._calculateMinMaxIntensity(filters);
        });
        return;
    }

    constructCalendar() {
        this.days = [];  // Clear the days array
        this.innerHTML = ''; // Clear the inner HTML to avoid duplication
        const calendar = document.createDocumentFragment();

        if (this.showCollectionDetails) {
            this._buildAdminHeader(calendar);
        }

        if (this.message) {
            this._buildFlightRouteMessage(calendar)
        }

        const [monthTitles, monthWeekCounts] = this._createMonthTitles(true);
        const outboundMonths = this._buildMonths(monthWeekCounts, true);
        const numOutboundMonths = outboundMonths.length;
        var tripDurationText = '';
        if (this.tripDurationMin && this.tripDurationMax) {
            if (this.tripDurationMin == this.tripDurationMax) {
                if (this.tripDurationMin == 1) {
                    tripDurationText = `Trip: ${this.tripDurationMin} day`;
                } else {
                    tripDurationText = `Trip: ${this.tripDurationMin} days`;
                }
            } else {
                tripDurationText = `Trip: ${this.tripDurationMin} - ${this.tripDurationMax} days`;
            }
        }

        if (this.inlineSearchSummary) {
            const outboundMobileSearchSummary = document.createElement('mobile-search-summary') as MobileSearchSummary;
            outboundMobileSearchSummary.outbound = this.outbound;
            outboundMobileSearchSummary.outboundIATA = this.outboundIATA;
            outboundMobileSearchSummary.inbound = this.inbound;
            outboundMobileSearchSummary.inboundIATA = this.inboundIATA;
            outboundMobileSearchSummary.oneWay = true;
            outboundMobileSearchSummary.tripDuration = tripDurationText;
            outboundMobileSearchSummary.classList.add('search-summary');
            calendar.append(outboundMobileSearchSummary);

            if (!this.oneWay) {
                const inboundMobileSearchSummary = document.createElement('mobile-search-summary') as MobileSearchSummary;
                inboundMobileSearchSummary.outbound = this.inbound;
                inboundMobileSearchSummary.outboundIATA = this.inboundIATA;
                inboundMobileSearchSummary.inbound = this.outbound;
                inboundMobileSearchSummary.inboundIATA = this.outboundIATA;
                inboundMobileSearchSummary.oneWay = true;
                inboundMobileSearchSummary.tripDuration = tripDurationText;
                inboundMobileSearchSummary.classList.add('search-summary', `mobile-order-${numOutboundMonths}`);
                calendar.append(inboundMobileSearchSummary);
            }
        }

        if (this.oneWay) {
            this.classList.add("one-way");
            const numMonths = monthTitles.length;
            for (var i = 0; i < numMonths - 1; i += 2) {
                const classes_i = this.singleColumn ? [`order-${i}`, `mobile-order-${i}`] : [`mobile-order-${i}`];
                const classes_i1 = this.singleColumn ? [`order-${i+1}`, `mobile-order-${i+1}`] : [`mobile-order-${i+1}`];

                switch(i) {
                    case 2:
                        var cta = this.#ctas[0].cloneNode(true) as DocumentFragment;
                        cta.querySelector('div')!.classList.add(...classes_i);
                        calendar.appendChild(cta);

                        if (this.airline === 'BA') {
                            cta = this.#ctas[4].cloneNode(true) as DocumentFragment;
                            cta.querySelector('div')!.classList.add(...classes_i);
                            calendar.appendChild(cta);
                        }

                        break;
                    case 4:
                        var cta = this.#ctas[3].cloneNode(true) as DocumentFragment;
                        cta.querySelector('div')!.classList.add(...classes_i);
                        calendar.appendChild(cta);
                        break;
                    case 8:
                        var cta = this.#ctas[1].cloneNode(true) as DocumentFragment;
                        cta.querySelector('div')!.classList.add(...classes_i);
                        calendar.appendChild(cta);
                        break;
                }

                monthTitles[i].classList.add(...classes_i);
                monthTitles[i + 1].classList.add(...classes_i1);
                outboundMonths[i].querySelector('table')!.classList.add(...classes_i);
                outboundMonths[i + 1].querySelector('table')!.classList.add(...classes_i1);

                calendar.appendChild(monthTitles[i]);
                calendar.appendChild(monthTitles[i + 1]);
                calendar.appendChild(outboundMonths[i]);
                calendar.appendChild(outboundMonths[i + 1]);
            }

            if (i < numMonths) {
                const classes_i = this.singleColumn ? [`order-${i}`, `mobile-order-${i}`] : [`mobile-order-${i}`];
                monthTitles[i].classList.add(...classes_i);
                outboundMonths[i].querySelector('table')!.classList.add(...classes_i);

                calendar.appendChild(monthTitles[i]);
                calendar.appendChild(outboundMonths[i]);
            }
        } else if (this.independentLegs) {
            this.classList.add("one-way");
            const [inboundMonthTitles, inboundMonthWeekCounts] = this._createMonthTitles(false);
            const inboundMonths = this._buildMonths(inboundMonthWeekCounts, false);

            [outboundMonths, inboundMonths].forEach((months, leg) => {
                const titles = leg === 0 ? monthTitles : inboundMonthTitles;
                months.forEach((month, i) => {
                    const title = titles[i];
                    const table = month.querySelector('table')!;
                    for (const element of [title, table]) {
                        element.classList.add('independent-leg-month', leg === 0 ? 'outbound-month' : 'inbound-month', `mobile-order-${i + (leg === 0 ? 0 : numOutboundMonths)}`);
                    }
                    title.style.setProperty('--month-row', (i * 2 + 1).toString());
                    table.style.setProperty('--month-row', (i * 2 + 2).toString());
                    calendar.appendChild(title);
                    calendar.appendChild(month);
                });
            });
        } else {
            this.classList.remove("one-way");
            const inboundMonths = this._buildMonths(monthWeekCounts, false);

            zip(monthTitles, outboundMonths, inboundMonths).forEach(([monthTitle, outboundMonth, inboundMonth], i) => {
                switch(i) {
                    case 1:
                        calendar.appendChild(this.#ctas[0].cloneNode(true));
                        
                        if (this.airline === 'BA') {
                            calendar.appendChild(this.#ctas[4].cloneNode(true));
                        }
                        break;
                    case 2:
                        calendar.appendChild(this.#ctas[3].cloneNode(true));
                        break;
                    case 4:
                        calendar.appendChild(this.#ctas[1].cloneNode(true));
                        break;
                    case 7:
                        calendar.appendChild(this.#ctas[2].cloneNode(true));
                        break;
                }

                calendar.appendChild(monthTitle);
                calendar.appendChild(outboundMonth);
                calendar.appendChild(inboundMonth);
            });
        }

        if (this.airline == 'CX') {
            this._buildCathayMonthMessage(calendar);
        }

        this.appendChild(calendar);
    }

    getTripDurationHighlightDates(date: Date, hoveredOutbound: boolean): Set<string> {
        const min = this.tripDurationMin;
        const max = this.tripDurationMax;
        const result = new Set<string>();

        if (min == null || max == null) {
            return result;
        }

        for (let days = min; days <= max; days++) {
            const candidate = new Date(date);
            candidate.setUTCDate(candidate.getUTCDate() + (hoveredOutbound ? days : -days));
            result.add(candidate.toISOString().slice(0, 10));
        }

        return result;
    }

    highlightDates(dates: Set<string>, outbound: boolean) {
        this.days.forEach(day => {
            if (day.outbound == outbound && day.date) {
                const key = day.date.toISOString().slice(0, 10);
                day.svg?.classList.toggle('trip-duration-highlight', dates.has(key));
            }
        });
    }

    clearTripDurationHighlight() {
        this.days.forEach(day => {
            day.svg?.classList.remove('trip-duration-highlight');
        });
    }

    private _buildAdminHeader(calendar: DocumentFragment) {
        const adminTitle = document.createElement("h2");
        adminTitle.classList.add("admin-title");
        adminTitle.textContent = "Admin Details";
        calendar.appendChild(adminTitle);

        const adminHeader = document.createElement("template");
        adminHeader.innerHTML = 'dataCollectionSources' in this.#data[0] ? this.#data[0].dataCollectionSources : '';
        calendar.appendChild(adminHeader.content);

        if (!this.oneWay) {
            const adminHeader = document.createElement("template");
            adminHeader.innerHTML ='dataCollectionSources' in this.#data[1] ? this.#data[1].dataCollectionSources : '';
            calendar.appendChild(adminHeader.content);
        }
    }

    private _buildFlightRouteMessage(calendar: DocumentFragment) {
        const message = document.createElement("div");
        message.classList.add("route-message");
        message.innerHTML = this.message!;
        calendar.appendChild(message)
    }

    private _createMonthTitles(outbound: boolean): [HTMLHeadingElement[], number[]] {
        const monthTitleTemplate = document.getElementById('availability-calendar-month-heading-template') as HTMLTemplateElement;
        const monthTitleNode = document.importNode(monthTitleTemplate.content, true);

        let curDate = new Date(this.startDate(outbound));
        let curMonth = null;
        let monthTitles = [];
        let monthWeekCounts = [];

        while (curDate <= this.endDate(outbound)) {
            if (curMonth != curDate.getUTCMonth()) {
                curMonth = curDate.getUTCMonth();

                const monthTitle = monthTitleNode.cloneNode(true) as HTMLTemplateElement;
                const heading = monthTitle.querySelector('.month-title') as HTMLHeadingElement;
                heading.id = 'month-title-' + curDate.getUTCFullYear() + '-' + (curDate.getUTCMonth() + 1).toString().padStart(2, '0');
                heading.classList.add("month-title");
                heading.textContent = formatDateAsUTC(curDate, 'monthyear');
                const windowNote = this._monthWindowNote(curDate, outbound);
                if (windowNote) {
                    heading.appendChild(windowNote);
                }

                monthTitles.push(heading);
                monthWeekCounts.push(this._weekCount(curDate.getUTCFullYear(), curMonth, outbound));
            }
            curDate = new Date(curDate.getTime() + 60 * 60 * 24 * 1000)
        }
        return [monthTitles, monthWeekCounts];
    }

    private _collectionWindows(outbound: boolean) {
        const data = this.#data?.[outbound ? 0 : 1];
        if (!data || !('collectionWindows' in data) || !data.collectionWindows?.length) {
            return null;
        }
        return data.collectionWindows;
    }

    private _monthWindowNote(curDate: Date, outbound: boolean): HTMLElement | null {
        if (!this.showCollectionDetails) {
            return null;
        }
        const shared = !this.oneWay && !this.independentLegs;
        const primary = this._collectionWindows(shared ? true : outbound);
        const secondary = shared ? this._collectionWindows(false) : null;
        const markers = primary ?? secondary;
        if (!markers) {
            return null;
        }

        const year = curDate.getUTCFullYear();
        const month = curDate.getUTCMonth();
        const monthStart = Date.UTC(year, month, 1);
        const monthEnd = Date.UTC(year, month + 1, 0);
        const lastDay = new Date(monthEnd).getUTCDate();
        const lines = markers.flatMap((marker) => {
            const span = this._windowMonthSpan(marker, monthStart, monthEnd);
            if (!span) {
                return [];
            }
            const other = secondary?.find((item) => item.code === marker.code);
            return [this._windowLine(marker, other, span, lastDay, shared && secondary != null)];
        });
        if (lines.length == 0) {
            return null;
        }
        const note = document.createElement('span');
        note.className = 'month-window';
        lines.forEach((line) => note.appendChild(line));
        return note;
    }

    private _windowMonthSpan(marker: { opening: string, closing: string | null, idle: boolean }, monthStart: number, monthEnd: number) {
        const opening = this._utcDay(marker.opening);
        if (marker.idle || marker.closing == null) {
            if (monthEnd < opening) {
                return null;
            }
            return { from: Math.max(monthStart, opening), to: monthEnd };
        }
        const closing = this._utcDay(marker.closing);
        const from = Math.max(monthStart, opening);
        const to = Math.min(monthEnd, closing);
        if (from > to) {
            return null;
        }
        return { from, to };
    }

    private _windowLine(
        marker: { letter: string, name: string, dates: string, label: string, tone: string, title: string },
        other: { label: string, tone: string, title: string } | undefined,
        span: { from: number, to: number },
        lastDay: number,
        paired: boolean,
    ) {
        const line = document.createElement('span');
        line.className = 'month-window-line';
        const fromDay = new Date(span.from).getUTCDate();
        const toDay = new Date(span.to).getUTCDate();
        const days = fromDay == 1 && toDay == lastDay ? '' : (fromDay == toDay ? this._ordinal(toDay) : `${fromDay}–${this._ordinal(toDay)}`);
        const letter = document.createElement('span');
        letter.className = 'month-window-letter';
        letter.textContent = `${marker.letter}-${marker.name}`;
        letter.title = `${marker.name} · ${marker.dates}`;
        line.appendChild(letter);
        if (days) {
            const daySpan = document.createElement('span');
            daySpan.className = 'month-window-days';
            daySpan.textContent = days;
            line.appendChild(daySpan);
        }
        line.appendChild(this._windowAge(paired ? `out ${marker.label}` : marker.label, marker.tone, marker.title));
        if (paired && other) {
            const separator = document.createElement('span');
            separator.className = 'month-window-sep';
            separator.textContent = '|';
            line.append(separator, this._windowAge(`in ${other.label}`, other.tone, other.title));
        }
        return line;
    }

    private _ordinal(day: number) {
        const teen = day % 100;
        if (teen >= 11 && teen <= 13) {
            return `${day}th`;
        }
        switch (day % 10) {
            case 1: return `${day}st`;
            case 2: return `${day}nd`;
            case 3: return `${day}rd`;
            default: return `${day}th`;
        }
    }

    private _windowAge(text: string, tone: string, title: string) {
        const age = document.createElement('span');
        age.className = `month-window-age${tone ? ` ${tone}` : ''}`;
        age.textContent = text;
        age.title = title;
        return age;
    }

    private _utcDay(iso: string) {
        const [year, month, day] = iso.split('-').map((part) => parseInt(part, 10));
        return Date.UTC(year, month - 1, day);
    }

    private _weekCount(year: number, month_number: number, outbound: boolean): number {
        if (new Date(this.startDate(outbound)).getUTCMonth() == month_number) {
            var firstOfMonth = new Date(this.startDate(outbound));
        } else {
            var firstOfMonth = new Date(Date.UTC(year, month_number, 1));
        }

        if (new Date(this.endDate(outbound)).getUTCMonth() == month_number) {
            var lastOfMonth = new Date(this.endDate(outbound));
        } else {
            var lastOfMonth = new Date(Date.UTC(year, month_number+1, 0));
        }

        var used = ((firstOfMonth.getUTCDay() + 6) % 7) + (lastOfMonth.getUTCDate() - firstOfMonth.getUTCDate() + 1);

        return Math.ceil( used / 7);
    }

    private _buildMonths(monthWeekCounts: number[], outbound: boolean) {
        this.firstDayWithData = true;  // Reset the first day with data flag when starting a new direction
        let curDate = new Date(this.startDate(outbound));
        let months = [];
        this.numMonths = 0;
        while (curDate <= this.endDate(outbound)) {
            if (this.singleColumn) {
                var maxWeeks = 0;
            // If we are on the last month and there are an odd number of months
            } else if (this.numMonths == monthWeekCounts.length - 1 && monthWeekCounts.length % 2 == 1){
                // Final month is on its own row, so no need to worry about balancing the number of weeks
                var maxWeeks = 0;
            } else {
                var maxWeeks = Math.max(monthWeekCounts[this.numMonths], (this.numMonths % 2 == 0) ? monthWeekCounts[this.numMonths + 1]: monthWeekCounts[this.numMonths - 1]);
            }
            let month;
            [month, curDate] = this._buildMonth(curDate, outbound, maxWeeks);
            months.push(month);
            this.numMonths++;
        }
        return months;
    }

    private _buildMonth(curDate: Date, outbound: boolean, maxWeeks: number): [DocumentFragment, Date] {
        const monthTemplate = document.getElementById('availability-calendar-month-template') as HTMLTemplateElement;
        const monthNode = document.importNode(monthTemplate.content, true);  // TODO Move to constructor so we don't have to load this from html for every month
        const monthBody = monthNode.querySelector('tbody') as HTMLTableSectionElement;

        const blankWeekTemplate = document.getElementById('availability-calendar-blank-week-template') as HTMLTemplateElement
        const blankWeekNode = document.importNode(blankWeekTemplate.content, true);  // TODO Move to constructor so we don't have to load this from html for every month

        let numWeeks = 0; // Keep a track of how many weeks we have added to the month
        let thisMonth = curDate.getUTCMonth();

        // If this is a one way calendar, and we are in the first month, and we are not a single column calendar
        if (this.oneWay && curDate.getTime() == this.startDate(outbound).getTime() && !this.singleColumn) {  // .getTime() is required when using == operator)
            // We need to fill in the month up to the start week with empty weeks
            // Calculate the number of empty weeks

            // Current date - number of days into week we are - 1 / 7
            let numPastWeeks = Math.ceil((curDate.getUTCDate() - ((curDate.getUTCDay() + 6) % 7) - 1) / 7);
            // Loop through each empty week
            for (let i = 0; i < numPastWeeks; i++) {
                monthBody.appendChild(blankWeekNode.cloneNode(true));  // Clone the blank week template
                numWeeks++;
            }
        }

        // While not end of month and not past end date
        while (curDate.getUTCMonth() == thisMonth && curDate <= this.endDate(outbound)) {
            let week;
            [week, curDate] = this._buildWeek(thisMonth, curDate, outbound);
            monthBody.appendChild(week);  // Append the week to the month
            numWeeks++;
        }

        // If this is a one way calendar, then ensure we have 6 total weeks so that all months are the same height
        while (this.oneWay && numWeeks < maxWeeks) {
            monthBody.appendChild(blankWeekNode.cloneNode(true));  // Clone the blank week template
            numWeeks++;
        }

        return [monthNode, curDate]
    }

    private _buildWeek(thisMonth: number, curDate: Date, outbound: boolean): [HTMLTableRowElement, Date] {
        const week = document.createElement("tr");

        // We need to fill in the week up to the curDate with inactive days
        // Calculate the number of inactive days
        var numPastDays = ((curDate.getUTCDay() - 1 + 7) % 7)  // Subtract 1 to move Monday to start of week rather than Sunday. Add 7 to prevent negative numbers. Modulo 7 to get the remainder.
        // Loop through each inactive day
        for (var i = 0; i < numPastDays; i++) {
            // Add an inactive day
            week.appendChild(document.createElement("td"));  // Add an empty cell to the week
        }

        do {
            // Add day
            week.appendChild(this._createDay(curDate, outbound));
            curDate = new Date(curDate.getTime() + 60 * 60 * 24 * 1000)
        // While not start of new week, and not end of month and not past end date
        } while (curDate.getUTCDay() != 1 && curDate.getUTCMonth() == thisMonth && curDate <= this.endDate(outbound));

        // If we are at the end of the calendar
        if (curDate > this.endDate(outbound)) {
            // We need to fill in the week past the end date with inactive days
            // Calculate the number of inactive days
            var numFutureDays = (7 - curDate.getUTCDay() + 1) % 7
            // Loop through each inactive day
            for (var i = 0; i < numFutureDays; i++) {
                // Add an inactive day
                week.appendChild(document.createElement("td"));  // Add an empty cell to the week
            }
        }

        return [week, curDate]
    }

    private _createDay(curDate: Date, outbound: boolean): AvailabilityCircle {
        const direction = this.#data[outbound ? 0 : 1];
        var day = direction.dates[0];

        // While the data has not reached the date we are displaying (Only occurs when there is data provided that is earlier than the first day of the calendar (startdate))
        while (direction.dates.length > 0 && new Date(day.startDate).getTime() < curDate.getTime()) {
            // Remove first element from array
            direction.dates.shift();
            day = direction.dates[0];
        }

        const availDate = document.createElement("avail-circle") as AvailabilityCircle;
        availDate.calendar = this;
        availDate.textContent = curDate.getUTCDate().toString(); // Set the text content to the date
        availDate.date = curDate;

        if (direction.dates.length > 0 && new Date(day.startDate).getTime() == curDate.getTime() && day.flights.length > 0) {
            if (this.firstDayWithData) {
                availDate.classList.add("first-day-with-data");
                this.firstDayWithData = false;
            }
            availDate.data = day; // Get the first date from the data
            availDate.passengerSpecificResults = 'passengerSpecificResults' in direction ? direction.passengerSpecificResults : false;
            availDate.outbound = outbound;

            // Set previous day and next date values
            if (this.days.length > 0) {
                const previousDay = this.days[this.days.length - 1];
                previousDay.nextDate = availDate;
                availDate.prevDate = previousDay;
            }

            // Admin only
            if (this.showCabinDiffs) {
                const circle = this.#adminCircle.cloneNode(true) as SVGCircleElement;
                if (day.newDate) {
                    circle.classList.add("new-date");
                    availDate.text?.before(circle);
                } else if (day.oldDate) {
                    circle.classList.add("old-date");
                    availDate.text?.before(circle);
                } else if (day.flights.some(flight => flight.economyDiff) || day.flights.some(flight => flight.premiumDiff) || day.flights.some(flight => flight.businessDiff) || day.flights.some(flight => flight.firstDiff)) {
                    circle.classList.add("changed-date");
                    availDate.text?.before(circle);
                }
            }

            this.days.push(availDate);
            // Remove first element from array
            direction.dates.shift();
        } else if (direction.dates.length > 0 && new Date(day.startDate).getTime() == curDate.getTime()) {
            availDate.data = day;
            // Remove first element from array
            direction.dates.shift();
        } else {
            availDate.data = null;
        }

        return availDate;
    }

    private _calculateMinMaxIntensity(filters: Filters) {
        // Only bother calculating if one cabin selected
        if (filters.cabins.length == 1) {
            let min: number | undefined = undefined;
            let max: number | undefined = undefined;
            for (const d of this.days) {
                const val = d.intensityRaw;
                if (val) {
                    if (min === undefined || val < min) min = val;
                    if (max === undefined || val > max) max = val;
                }
            }

            // Set CSS variables if min/max found
            if (min !== undefined && max !== undefined) {
                this.style.setProperty('--min-intensity', min.toString());
                this.style.setProperty('--max-intensity', max.toString());
            } else {
                this.style.removeProperty('--min-intensity');
                this.style.removeProperty('--max-intensity');
            }
        }
    }

    private _buildCathayMonthMessage(calendar: DocumentFragment) {
        const message = document.createElement("div");
        message.classList.add("cathay-month-message");
        message.innerHTML = "<p>Cathay Pacific Reward Seat Availability and Alerts cover 6 months.</p>";
        calendar.appendChild(message);
    }

    private _initialiseCabinCounts() {
        this.cabinCounts.set('outbound', new Map<Cabin, number[]>());
        this.cabinCounts.set('inbound', new Map<Cabin, number[]>());

        this.cabins.forEach((cabin) => {
            this.cabinCounts.get('outbound')!.set(cabin, new Array(this.numMonths).fill(0));
            this.cabinCounts.get('inbound')!.set(cabin, new Array(this.numMonths).fill(0));
        });
    }
}


customElements.define('year-calendar', Calendar);
